import { useState } from 'react';
import { Play } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { isVideoUrl } from '../lib/attachments';
import { MediaViewerDialog, type MediaViewerState } from './MediaViewerDialog';
import { safeMediaSrc } from '@/lib/mediaUrl';

/**
 * Feedback attachments grid (doc 09 §4.9, FEEDBACK only). Responsive tiles; type inferred by extension
 * (feedback attachments carry no typed field — §8 risk #9). Click opens the full-screen viewer with
 * image zoom / inline video.
 */
export function FeedbackAttachmentsCard({ attachments }: { attachments: string[] }) {
  const [viewer, setViewer] = useState<MediaViewerState | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Attachments</CardTitle>
      </CardHeader>
      <CardContent>
        {attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No attachments.</p>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-3">
            {attachments.map((url, i) => {
              const video = isVideoUrl(url);
              return (
                <button
                  key={`${url}-${i}`}
                  type="button"
                  onClick={() => setViewer({ url, isVideo: video, playbackId: null })}
                  className="relative aspect-square overflow-hidden rounded-lg border bg-muted"
                >
                  {video ? (
                    <>
                      <video src={url} className="size-full object-cover" muted preload="metadata" />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white">
                        <Play className="size-6" />
                      </span>
                    </>
                  ) : (
                    <img src={safeMediaSrc(url)} alt="" className="size-full object-cover" loading="lazy" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </CardContent>

      <MediaViewerDialog media={viewer} onClose={() => setViewer(null)} />
    </Card>
  );
}
